import apiClient from '../client.js'
import * as Sentry from '@sentry/vue'

// Rate limiting configuration
const rateLimits = new Map()
const RATE_LIMIT_WINDOW = 60000 // 1 minute
const DEFAULT_MAX_REQUESTS = 10

const checkRateLimit = (endpoint, maxRequests = DEFAULT_MAX_REQUESTS) => {
  const now = Date.now()
  const key = endpoint
  
  if (!rateLimits.has(key)) {
    rateLimits.set(key, { count: 1, windowStart: now })
    return true
  }
  
  const limit = rateLimits.get(key)
  
  // Reset window if expired
  if (now - limit.windowStart > RATE_LIMIT_WINDOW) {
    rateLimits.set(key, { count: 1, windowStart: now })
    return true
  }
  
  // Check if limit exceeded
  if (limit.count >= maxRequests) {
    Sentry.logger.warn('Auth API rate limit exceeded', {
      endpoint: endpoint,
      max_requests: maxRequests,
      window_ms: RATE_LIMIT_WINDOW
    })
    throw new Error(`Rate limit exceeded for ${endpoint}. Try again later.`)
  }
  
  limit.count++
  return true
}

export const authAPI = {
  // Authentication
  async register(userData) {
    checkRateLimit('register', 5)
    const response = await apiClient.post('/auth/register', userData)
    return response.data
  },

  async login(credentials) {
    checkRateLimit('login', 5)
    const response = await apiClient.post('/auth/login', credentials)
    return response.data
  },

  async logout() {
    checkRateLimit('logout', 20)
    const response = await apiClient.post('/auth/logout')
    return response.data
  },

  async logoutAll() {
    checkRateLimit('logoutAll', 10)
    const response = await apiClient.post('/auth/logout-all')
    return response.data
  },

  async forgotPassword(email) {
    checkRateLimit('forgotPassword', 3)
    const response = await apiClient.post('/auth/forgot-password', { email })
    return response.data
  },

  async resetPassword(data) {
    checkRateLimit('resetPassword', 5)
    const response = await apiClient.post('/auth/reset-password', data)
    return response.data
  },

  async changePassword(data) {
    checkRateLimit('changePassword', 5)
    const response = await apiClient.put('/auth/change-password', data)
    return response.data
  },

  async verifyEmail(token) {
    checkRateLimit('verifyEmail', 10)
    const response = await apiClient.post('/auth/verify-email', { token })
    return response.data
  },

  async resendVerificationEmail(email) {
    checkRateLimit('resendVerificationEmail', 3)
    const response = await apiClient.post('/auth/resend-verification', { email })
    return response.data
  },

  // Profile management
  async getProfile() {
    checkRateLimit('getProfile', 30)
    const response = await apiClient.get('/auth/profile')
    return response.data
  },

  async refreshToken() {
    checkRateLimit('refreshToken', 20)
    const response = await apiClient.post('/auth/refresh-token')
    return response.data
  },

  async switchAccountType(accountType, companyData = {}) {
    checkRateLimit('switchAccountType', 5)
    const response = await apiClient.put('/auth/switch-account-type', {
      account_type: accountType,
      ...companyData
    })
    return response.data
  },

  // Session management
  async getSessions() {
    checkRateLimit('getSessions', 15)
    const response = await apiClient.get('/auth/sessions')
    return response.data
  },

  async revokeSession(tokenId) {
    checkRateLimit('revokeSession', 10)
    const response = await apiClient.delete(`/auth/sessions/${tokenId}`)
    return response.data
  }
}
